"use client"

import { useEffect, useState } from "react"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../firebase"
import {
  FaUsers,
  FaEdit,
  FaPen,
  FaSpinner,
  FaExclamationTriangle,
  FaQuoteLeft,
  FaBook,
  FaCamera,
  FaStar,
  FaFeatherAlt,
  FaScroll,
} from "react-icons/fa"

const Editorial = () => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [visibleSections, setVisibleSections] = useState(new Set())
  const [activeCard, setActiveCard] = useState(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const fetchEditorialMembers = async () => {
      try {
        setLoading(true)
        const snapshot = await getDocs(collection(db, "committees/Editorial/members"))
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        // Sort by order field, then by designation priority (Head first, Co-Head second)
        const sortedData = data.sort((a, b) => {
          // First sort by order if it exists
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order
          }
          if (a.order !== undefined) return -1
          if (b.order !== undefined) return 1

          // Then sort by designation priority
          const designationPriority = { Head: 0, "Co-Head": 1 }
          const aPriority = designationPriority[a.designation] ?? 2
          const bPriority = designationPriority[b.designation] ?? 2

          if (aPriority !== bPriority) return aPriority - bPriority

          // Finally sort by name
          return a.name.localeCompare(b.name)
        })

        setMembers(sortedData)
      } catch (error) {
        console.error("Error fetching editorial members:", error)
        setError("Failed to load committee members")
      } finally {
        setLoading(false)
      }
    }

    fetchEditorialMembers()
  }, [])

  const highlights = [
    {
      icon: FaScroll,
      title: "Documentation",
      description: "Comprehensive record-keeping of all chapter activities",
      color: "from-emerald-500 to-emerald-700",
      bgPattern: "bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20",
    },
    {
      icon: FaFeatherAlt,
      title: "Content Creation",
      description: "Crafting engaging narratives and compelling stories",
      color: "from-teal-500 to-teal-700",
      bgPattern: "bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20",
    },
    {
      icon: FaCamera,
      title: "Event Coverage",
      description: "Real-time chronicling and visual storytelling",
      color: "from-cyan-500 to-cyan-700",
      bgPattern: "bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20",
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-teal-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-teal-400/20 to-emerald-400/20 rounded-full mix-blend-multiply filter blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-gradient-to-r from-cyan-400/10 to-emerald-400/10 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        {/* Creative Header */}
        <div
          data-animate
          id="header"
          className={`text-center mb-20 transform transition-all duration-1000 ${
            visibleSections.has("header") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full blur-lg opacity-30 animate-pulse"></div>
            <div className="relative w-24 h-24 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center shadow-2xl">
              <FaEdit className="w-12 h-12 text-white" />
            </div>
          </div>

          <h1 className="text-6xl md:text-8xl font-black mb-6 relative">
            <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent animate-gradient-x">
              Editorial
            </span>
            <br />
            <span className="bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent animate-gradient-x-reverse">
              Committee
            </span>
          </h1>

          <div className="flex justify-center items-center space-x-4 mb-8">
            <div className="w-16 h-1 bg-gradient-to-r from-emerald-500 to-transparent rounded-full"></div>
            <FaStar className="w-6 h-6 text-yellow-500 animate-spin-slow" />
            <div className="w-16 h-1 bg-gradient-to-l from-teal-500 to-transparent rounded-full"></div>
          </div>

          <p className="text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
            Crafting compelling narratives and preserving the chapter's journey through the art of storytelling
          </p>
        </div>

        {/* Creative Highlights */}
        <div
          data-animate
          id="highlights"
          className={`mb-20 transform transition-all duration-1000 delay-200 ${
            visibleSections.has("highlights") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden rounded-3xl ${highlight.bgPattern} p-8 hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:rotate-1`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="absolute top-0 left-0 w-32 h-32 opacity-10">
                  <div
                    className={`w-full h-full bg-gradient-to-br ${highlight.color} rounded-full transform -translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-700`}
                  ></div>
                </div>

                <div className="relative z-10">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${highlight.color} rounded-2xl mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-12`}
                  >
                    <highlight.icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300">
                    {highlight.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{highlight.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Creative About Section */}
        <div
          data-animate
          id="about"
          className={`mb-20 transform transition-all duration-1000 delay-400 ${
            visibleSections.has("about") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5 rounded-3xl transform -rotate-1"></div>
            <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"></div>

              <div className="p-12">
                <div className="flex flex-col lg:flex-row items-start space-y-8 lg:space-y-0 lg:space-x-12">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl blur-lg opacity-30"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center shadow-xl">
                        <FaQuoteLeft className="w-10 h-10 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <h2 className="text-4xl md:text-5xl font-bold mb-8">
                      <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                        Our Mission
                      </span>
                    </h2>

                    <div className="space-y-6 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                      <p className="relative pl-6">
                        <span className="absolute left-0 top-2 w-2 h-2 bg-emerald-500 rounded-full"></span>
                        The Editorial Committee serves as the storyteller of the IIChE Student Chapter, weaving together
                        diverse activities into compelling narratives that inspire and inform.
                      </p>

                      <p className="relative pl-6">
                        <span className="absolute left-0 top-2 w-2 h-2 bg-teal-500 rounded-full"></span>
                        We meticulously document every milestone, achievement, and noteworthy moment, ensuring that the
                        Chapter's rich history is preserved for posterity.
                      </p>

                      <p className="relative pl-6">
                        <span className="absolute left-0 top-2 w-2 h-2 bg-cyan-500 rounded-full"></span>
                        Through real-time event coverage and collaborative content creation, we bridge the gap between
                        activities and their wider impact on the community.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Creative Team Section */}
        <div
          data-animate
          id="team"
          className={`transform transition-all duration-1000 delay-600 ${
            visibleSections.has("team") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Meet Our Team
              </span>
            </h2>

            <div className="flex justify-center items-center space-x-4 mb-8">
              <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-transparent rounded-full"></div>
              <FaBook className="w-8 h-8 text-emerald-500 animate-bounce" />
              <div className="w-20 h-1 bg-gradient-to-l from-teal-500 to-transparent rounded-full"></div>
            </div>

            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Passionate storytellers and meticulous documentarians
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full blur-lg opacity-30 animate-pulse"></div>
                <FaSpinner className="relative w-16 h-16 text-emerald-600 animate-spin" />
              </div>
              <p className="text-2xl text-gray-600 dark:text-gray-400 font-medium mt-6">Loading our creative team...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 max-w-md mx-auto">
                <FaExclamationTriangle className="w-16 h-16 text-red-600 dark:text-red-400 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold text-red-800 dark:text-red-200 mb-4">
                  Oops! Something went wrong
                </h3>
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-20">
              <div className="relative inline-block mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full blur-lg opacity-20"></div>
                <FaUsers className="relative w-20 h-20 text-gray-400" />
              </div>
              <h3 className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">Team Coming Soon</h3>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                Our talented writers will be featured here soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {members.map((member, index) => (
                <div
                  key={member.id}
                  className="group relative"
                  style={{ animationDelay: `${index * 150}ms` }}
                  onMouseEnter={() => setActiveCard(member.id)}
                  onMouseLeave={() => setActiveCard(null)}
                >
                  <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:rotate-1">
                    {/* Animated Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Image Section */}
                    <div className="relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10"></div>
                      <img
                        src={member.photoUrl || "/placeholder.svg"}
                        alt={member.name}
                        className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          e.target.src = "/placeholder.svg"
                        }}
                      />

                      {/* Floating Designation Badge */}
                      <div className="absolute top-6 right-6 z-20">
                        <div
                          className={`px-4 py-2 rounded-full text-sm font-bold shadow-lg backdrop-blur-sm ${
                            member.designation === "Head"
                              ? "bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white"
                              : member.designation === "Co-Head"
                                ? "bg-gradient-to-r from-blue-500/90 to-cyan-500/90 text-white"
                                : "bg-white/90 text-gray-800"
                          }`}
                        >
                          {member.designation}
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="relative z-10 p-8">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur-md opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                          <div className="relative w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
                            <FaPen className="w-7 h-7 text-white" />
                          </div>
                        </div>

                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300">
                            {member.name}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 font-medium">Committee Member</p>
                        </div>
                      </div>

                      {/* Animated Underline */}
                      <div className="w-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full group-hover:w-full transition-all duration-500"></div>
                    </div>

                    {/* Hover Overlay Effect */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ${activeCard === member.id ? "opacity-100" : ""}`}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gradient-x-reverse {
          0%, 100% { background-position: 100% 50%; }
          50% { background-position: 0% 50%; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
        .animate-gradient-x { 
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-gradient-x-reverse { 
          background-size: 200% 200%;
          animation: gradient-x-reverse 3s ease infinite;
        }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
      `}</style>
    </div>
  )
}

export default Editorial
